import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { Info } from '@mui/icons-material';
import { BoxWrapper, GridWrapper } from 'shared/components/data-display/GridFormatter';
import { dateString, getStripeUrl, priceToString } from 'shared/utils';
import { NewTabLink } from 'shared/components/abstract/interactables';
import { CardControls, CardSection } from 'shared/components/cardControls';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { getCouponPath } from 'shared/routes/coupon';
import { viewPath as voucherViewPath } from 'shared/routes/voucher';
import { getSubscription } from 'shared/api/subscription';
import { getBuildingUpgrades } from 'shared/api/upgrades';
import { useNotification, useRequest } from 'shared/hooks';
import { formatBuildingUpgrade, formatTenantName } from 'shared/utils/formatters';
import MouseOverPopover from 'shared/components/MouseOverPopover';
import ListGroupItem from 'shared/components/styled/listGroupItem';
import SubscriptionManager from './SubscriptionManager';

function SubscriptionStatus({ sub }) {
  let bg;
  let text;
  const {
    status,
    cancelAtPeriodEnd,
  } = sub;

  if (sub.isVoucherService) {
    text = 'voucher service';
    bg = 'success';
  } else if (sub.isTrialing) {
    bg = 'success';
  } else if (cancelAtPeriodEnd) {
    text = 'cancel at period end';
    // `warning` is transient: the cancellation is pending, not over.
    bg = 'warning';
  } else if (status === 'active') {
    bg = 'primary';
  } else {
    // Problem states: past_due, canceled, unpaid, incomplete.
    bg = 'error';
  }

  return (
    <Typography component="span" color={bg}>
      {(text && text.toUpperCase()) || status.toUpperCase()}
    </Typography>
  );
}

const subscriptionDescriptionsColumn1 = [
  {
    label: 'Subscription Status',
    formatter: (sub) => (sub !== undefined ? <SubscriptionStatus sub={sub} /> : 'No Subscription'),
  },
  {
    label: 'Service Level',
    formatter: (sub) => (
      sub.isVoucherService
        ? sub.description
        : `${sub.description} for ${priceToString(sub.price)}/month`
    ),
  },
  {
    label: 'Stripe Customer',
    formatter: (sub) => (
      <NewTabLink
        link={getStripeUrl('customers', sub.customerId)}
        text={sub.customerId}
      />
    ),
  },
  {
    label: 'Stripe Subscription',
    formatter: (sub) => <NewTabLink link={getStripeUrl('subscriptions', sub.id)} text={sub.id} />,
  },
];

const subscriptionDescriptionsColumn2 = [
  {
    label: 'Next Payment',
    skipWhen: (sub) => sub.cancelAtPeriodEnd || sub.status === 'canceled' || sub.status === 'incomplete_expired',
    formatter: (sub) => `${priceToString(sub.discountedPrice)} on ${dateString(sub.periodEnd)}`,
  },
  {
    label: 'Cancel On',
    skipWhen: (sub) => !sub.cancelAtPeriodEnd,
    formatter: (sub) => dateString(sub.cancelAt),
  },
  {
    label: 'Voucher',
    skipWhen: (sub) => !sub.isVoucherService,
    formatter: (sub) => (
      <Link to={`${voucherViewPath}?voucher=${sub.voucher}`}>
        {sub.voucher}
      </Link>
    ),
  },
  {
    label: 'Coupon',
    skipWhen: (sub) => !sub.couponId,
    formatter: (sub) => (
      <Link to={getCouponPath(sub.couponId)}>
        {`${sub.couponName} "${sub.couponId}"`}
      </Link>
    ),
  },
  {
    label: 'Created',
    formatter: (sub) => dateString(sub.created),
  },
];

/**
 * @param {Object} props
 * @param {Function} props.reloadTenant - Function to reload tenant data
 * @param {Object} props.tenantState - Tenant state object
 */
export default function TenantInternetSubscription(props) {
  const {
    reloadTenant,
    tenantState,
  } = props;

  const {
    request,
    isLoading,
  } = useRequest();
  const buildingId = tenantState.building && tenantState.building.id;

  const [subscription, setSubscription] = useState(undefined);
  const [buildingUpgrades, setBuildingUpgrades] = useState([]);

  const { pushError } = useNotification();

  const { data: tenant } = tenantState;

  useEffect(() => {
    const { subscription: subscriptionRecord } = tenant;

    async function loadSubscription() {
      const stripeSubscription = await request(getSubscription(tenant.id));
      if (stripeSubscription === undefined) return;
      if (stripeSubscription === null) {
        setSubscription(null);
        return;
      }

      const coupon = stripeSubscription.discounts.length > 0
        ? stripeSubscription.discounts[0].source?.coupon : null;
      const { items: { data } } = stripeSubscription;
      const { plan } = (data.length > 0 && data[0]) || {};
      const { amount: price } = plan || {};
      let discountedPrice = price;
      if (coupon) {
        if (coupon.amount_off) {
          discountedPrice = price - coupon.amount_off;
        } else {
          discountedPrice = price - (price * (coupon.percent_off * 0.01));
        }
      }

      // Merge our record with Stripe's subscription object for easier data access.
      // Dates from Stripe are in unix seconds, which need to be multiplied by 1000 for JS.
      setSubscription({
        cancelAt: stripeSubscription.cancel_at * 1000,
        cancelAtPeriodEnd: stripeSubscription.cancel_at_period_end,
        couponId: coupon ? coupon.id : null,
        couponName: coupon ? coupon.name : null,
        couponPercentOff: coupon ? coupon.percent_off : null,
        created: stripeSubscription.created * 1000,
        customerId: stripeSubscription.customer.id,
        description: subscriptionRecord.plan.product.qos.description,
        discountedPrice,
        id: stripeSubscription.id,
        isTrialing: stripeSubscription.status === 'trialing' && !subscriptionRecord.voucher,
        isVoucherService: !!subscriptionRecord.voucher,
        periodEnd: stripeSubscription.items.data[0].current_period_end * 1000,
        periodStart: stripeSubscription.current_period_start * 1000,
        planId: subscriptionRecord.plan.id,
        price,
        qosId: subscriptionRecord.plan.product.qos.id,
        status: stripeSubscription.status,
        tenantEmail: tenant.email,
        trialEnd: stripeSubscription.trial_end * 1000,
        trialStart: stripeSubscription.trial_start * 1000,
        voucher: subscriptionRecord.voucher,
      });
    }

    if (tenant.subscription) {
      loadSubscription();
    } else {
      setSubscription(null);
    }
  }, [request, tenant]);

  useEffect(() => {
    (async () => {
      if (!subscription || subscription.isVoucherService) return;

      const currentResponse = await request(getBuildingUpgrades(buildingId));

      if (currentResponse === undefined) return;
      setBuildingUpgrades(currentResponse);
    })();
  }, [request, buildingId, subscription]);

  // Push an error when the subscription's plan is no longer offered.
  useEffect(() => {
    if (!subscription || buildingUpgrades.length === 0) return;

    if (!buildingUpgrades.find((u) => u.plan.id === subscription.planId)) {
      pushError('This subscription\'s plan is no longer offered.');
      const upgrades = [...buildingUpgrades];

      // Add the missing upgrade into the list so that the dropdown displays correctly.
      upgrades.push({
        plan: {
          id: subscription.planId,
          price: subscription.price,
          product: { qos: { description: subscription.description } },
        },
      });
      setBuildingUpgrades(upgrades.sort((a, b) => a.plan.price > b.plan.price));
    }
  }, [pushError, subscription, buildingUpgrades]);

  if (subscription === undefined) {
    return <CardSection><CardControls editEnabled={false} /></CardSection>;
  }

  return (
    <CardSection editEnabled={tenantState.editEnabled}>
      <CardControls
        disabled={isLoading}
        editEnabled={tenantState.editEnabled}
      />
      <BoxWrapper>
        <GridWrapper
          size={{ lg: 6, xs: 12 }}
          title="Subscription Info"
          titleIcon={(buildingUpgrades?.length > 0) ? (
            <Grid>
              <MouseOverPopover
                title="Available Upgrades"
                content={buildingUpgrades.map((upgrade) => (
                  <ListGroupItem key={upgrade.id}>
                    {`${formatBuildingUpgrade(upgrade)} `}
                  </ListGroupItem>
                ))}
                Icon={Info}
              />
            </Grid>
          ) : ''}
          iconJustification="start"
        >
          <Grid container spacing={2}>
            <Grid size={6}>
              <DataFormatter
                data={subscription}
                descriptions={subscriptionDescriptionsColumn1}
                fallback="No Subscription"
              />
            </Grid>
            <Grid size={6}>
              <DataFormatter
                data={subscription}
                descriptions={subscriptionDescriptionsColumn2}
                fallback="" /* Set this to empty to prevent default prop */
              />
            </Grid>
          </Grid>
        </GridWrapper>
        {tenantState.editEnabled && (
          <GridWrapper
            size={{ lg: 6, xs: 12 }}
            title="Edit"
          >
            <SubscriptionManager
              building={tenantState.building || {}}
              buildingUpgrades={buildingUpgrades}
              reloadTenant={reloadTenant}
              subscription={subscription || {}}
              tenantEmail={tenant.email}
              tenantId={tenant.id}
              tenantName={formatTenantName(tenant)}
            />
          </GridWrapper>
        )}
      </BoxWrapper>
    </CardSection>
  );
}
